import type { ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useUI } from "../store";
import type { AuthSession } from "../types";
import { LanguageSwitch } from "./LanguageSwitch";
import { OrganizerLockup } from "./OrganizerLockup";
import { Icon, type IconName } from "./Icon";

// The chrome every view sits inside, on the dark-navy / cyber-mint system.
//
// `app-shell`, `app-header` and `app-main` are kept as class names even though
// their styling is now utilities: terminal fullscreen and presentation mode both
// hide the header with `.app-header{visibility:hidden}`, and the @media print
// block collapses all three. They are behaviour hooks now, not style hooks —
// their visual rules were removed from styles.css when this was converted,
// because unlayered CSS outranks Tailwind's layered utilities and would have
// won against the classes below.

// Breakpoints are the ones the old stylesheet used (560px / 960px) rather than
// Tailwind's md/lg, so the header still sheds width at exactly the sizes it was
// tuned for — at 800px wide the brand text plus seven nav items overflows.
const navLink = "flex items-center gap-[7px] whitespace-nowrap border-b-2 px-[7px] text-xs no-underline transition-colors min-[560px]:px-[9px] min-[560px]:text-sm min-[960px]:px-3 min-[960px]:text-base";
const headerButton = "inline-flex h-[34px] items-center justify-center gap-[7px] rounded-lg border border-line-soft px-[9px] text-ink transition-colors hover:border-ink-faint hover:text-ink-strong";

export function AppShell({ session, children }: { session: AuthSession; children: ReactNode }) {
  const navigate = useNavigate();
  const { theme, setTheme, language } = useUI();
  const text = language === "th" ? {
    nav: ["หน้าแรก", "แล็บ", "คู่มือ", "Topology", "เครื่องมือ", "ติดตามระบบ", "ผู้สอน"],
    menu: "เมนูหลัก", language: "ภาษา", theme: "สลับธีม", logout: "ออกจากระบบ"
  } : {
    nav: ["Home", "Lab", "Docs", "Topology", "Tools", "Monitoring", "Instructor"],
    menu: "Main navigation", language: "Language", theme: "Toggle theme", logout: "Sign out"
  };
  const nav = [
    ["/home", text.nav[0], ["participant", "instructor"], "home"], ["/lab", text.nav[1], ["participant", "instructor"], "lab"], ["/docs", text.nav[2], ["participant", "instructor"], "docs"],
    ["/topology", text.nav[3], ["participant", "instructor", "monitor"], "topology"], ["/tools", text.nav[4], ["participant", "instructor", "monitor"], "tools"], ["/monitor", text.nav[5], ["participant", "instructor", "monitor"], "monitor"], ["/instructor", text.nav[6], ["instructor"], "instructor"]
  ] as ReadonlyArray<readonly [string, string, readonly string[], IconName]>;
  return <div className="app-shell grid h-dvh grid-rows-[56px_minmax(0,1fr)] overflow-hidden">
    {/* min-w-0: as a grid item the header's automatic minimum size is its
        min-content width, which the nav and action buttons push past the
        viewport — that widened the whole grid column and dragged the page into
        horizontal overflow. */}
    <header className="app-header sticky top-0 z-20 flex min-w-0 items-center gap-2 border-b border-line bg-pane-left px-[10px] min-[960px]:gap-5 min-[960px]:px-[18px]">
      <div className="flex shrink-0 items-center gap-[9px] font-bold whitespace-nowrap text-ink-strong"><OrganizerLockup variant="compact" /><span className="hidden min-[960px]:inline">DNSSEC Workshop</span></div>
      <nav className="flex min-w-0 self-stretch overflow-x-auto" aria-label={text.menu}>
        {nav.filter(([, , roles]) => roles.includes(session.role as never)).map(([to, label, , icon]) =>
          <NavLink key={to} to={to} end={to === "/home"} className={({ isActive }) => `${navLink} ${isActive ? "border-accent text-ink-strong" : "border-transparent text-ink-muted hover:text-ink-strong"}`}><Icon name={icon} />{label}</NavLink>)}
      </nav>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <span className="hidden text-[0.8125rem] text-ink-muted min-[960px]:inline">{session.group_id || session.username}</span>
        <LanguageSwitch />
        <button className={`${headerButton} w-[34px] px-0`} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={text.theme}><Icon name={theme === "dark" ? "theme-light" : "theme-dark"} /></button>
        <button className={headerButton} onClick={async () => { await api("/api/auth/logout", { method: "POST", body: "{}" }); navigate(0); }}><Icon name="logout" /><span className="hidden sm:inline">{text.logout}</span></button>
      </div>
    </header>
    <main className="app-main min-h-0 overflow-auto">{children}</main>
  </div>;
}

/** Public reading chrome: no group/session data is shown and no private tools are linked. */
export function PublicShell({ children, signedIn = false }: { children: ReactNode; signedIn?: boolean }) {
  const { theme, setTheme, language } = useUI();
  const text = language === "th"
    ? { menu: "เมนูสาธารณะ", resources: "เอกสาร", docs: "คู่มือ", login: signedIn ? "กลับหน้าหลัก" : "เข้าสู่ระบบ", language: "ภาษา", theme: "สลับธีม" }
    : { menu: "Public navigation", resources: "Resources", docs: "Docs", login: signedIn ? "Back to home" : "Sign in", language: "Language", theme: "Toggle theme" };
  return <div className="app-shell grid h-dvh grid-rows-[56px_minmax(0,1fr)] overflow-hidden">
    <header className="app-header sticky top-0 z-20 flex min-w-0 items-center gap-2 border-b border-line bg-pane-left px-[10px] min-[960px]:gap-5 min-[960px]:px-[18px]">
      <div className="flex shrink-0 items-center gap-[9px] font-bold whitespace-nowrap text-ink-strong">
        <OrganizerLockup variant="compact" />
        <span className="hidden min-[960px]:inline">DNSSEC Workshop</span>
      </div>
      <nav className="flex min-w-0 self-stretch overflow-x-auto" aria-label={text.menu}>
        <NavLink to="/resources" className={({ isActive }) => `${navLink} ${isActive ? "border-accent text-ink-strong" : "border-transparent text-ink-muted hover:text-ink-strong"}`}>
          <Icon name="dashboard" />{text.resources}
        </NavLink>
        <NavLink to="/docs" className={({ isActive }) => `${navLink} ${isActive ? "border-accent text-ink-strong" : "border-transparent text-ink-muted hover:text-ink-strong"}`}>
          <Icon name="docs" />{text.docs}
        </NavLink>
      </nav>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <LanguageSwitch />
        <button className={`${headerButton} w-[34px] px-0`} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={text.theme}>
          <Icon name={theme === "dark" ? "theme-light" : "theme-dark"} />
        </button>
        <Link className={headerButton} to={signedIn ? "/home" : "/login"}>
          <Icon name="login" /><span className="hidden sm:inline">{text.login}</span>
        </Link>
      </div>
    </header>
    <main className="app-main min-h-0 overflow-auto">{children}</main>
  </div>;
}
